/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TransactionReceipt } from "@saberhq/solana-contrib";
import {
  getATAAddress,
  TokenAugmentedProvider,
  u64,
} from "@saberhq/token-utils";
import { PublicKey } from "@solana/web3.js";
import base58 from "bs58";
import { Command } from "commander";
import { printTable } from "console-table-printer";
import invariant from "tiny-invariant";

import * as pack from "../package.json";
import type { RedeemerData } from "../src";
import { QUARRY_ADDRESSES, QUARRY_CODERS } from "../src";
import {
  catchFinallyExit,
  confirmTx,
  makeSDK,
  printObjectJSON,
  receiptLog,
} from "./utils";

export const program = new Command().name("redeemer");
program.version(pack.version);

program
  .command("new")
  .description("Create a quarry_redeemer:Redeemer")
  .requiredOption("--iou-mint <iouMint>", "The IOU token mint address")
  .requiredOption(
    "--redemption-mint <redemptionMint>",
    "The redemption token mint address"
  )
  .action((arg) => {
    catchFinallyExit(
      createRedeemer(
        new PublicKey(arg.iouMint),
        new PublicKey(arg.redemptionMint)
      )
    );
  });

program
  .command("list")
  .description("Fetch the quarry_redeemer:Redeemer list")
  .action(() => {
    catchFinallyExit(fetchRedeemers());
  });

program
  .command("redeem_tokens")
  .description("Redeems some of a user's tokens from the redemption vault.")
  .requiredOption("--iou-mint <iouMint>", "The IOU token mint address")
  .requiredOption(
    "--redemption-mint <redemptionMint>",
    "The redemption token mint address"
  )
  .requiredOption("-a, --amount <amount>", "The amount tokens you want redeem")
  .action((arg) => {
    catchFinallyExit(
      redeemTokens(
        new PublicKey(arg.iouMint),
        new PublicKey(arg.redemptionMint),
        new u64(arg.amount)
      )
    );
  });

async function createRedeemer(
  iouMint: PublicKey,
  redemptionMint: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const res = await sdk.createRedeemer({
    iouMint,
    redemptionMint,
  });
  const receipt = await confirmTx(res.tx);

  printLog(receipt);

  return receipt;
}

async function fetchRedeemers() {
  const sdk = makeSDK();
  const accounts = await sdk.provider.connection.getProgramAccounts(
    QUARRY_ADDRESSES.Redeemer,
    {
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: base58.encode(
              QUARRY_CODERS.Redeemer.accounts.redeemer.discriminator
            ),
          },
        },
      ],
    }
  );
  const list = [];
  const tokenProvider = new TokenAugmentedProvider(sdk.provider);
  for (const accountInfo of accounts) {
    const redeemer = QUARRY_CODERS.Redeemer.coder.accounts.decode<RedeemerData>(
      "Redeemer",
      accountInfo.account.data
    );
    const redeemerToken = await tokenProvider.fetchATA(
      redeemer.redemptionMint,
      accountInfo.pubkey
    );
    invariant(redeemerToken !== null, "The redeemer token is null");
    list.push({
      redeemer: accountInfo.pubkey.toBase58(),
      redemptionMint: redeemer.redemptionMint.toBase58(),
      iouMint: redeemer.iouMint.toBase58(),
      totalTokensRedeemed: redeemer.totalTokensRedeemed.toString(),
      redemptionTokenBalance: redeemerToken.amount.toString(),
    });
  }
  printTable(list);
}

async function redeemTokens(
  iouMint: PublicKey,
  redemptionMint: PublicKey,
  amount: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const redeemer = await sdk.loadRedeemer({
    iouMint,
    redemptionMint,
  });
  const iouATA = await getATAAddress({
    mint: iouMint,
    owner: sdk.provider.wallet.publicKey,
  });
  const redemptionATA = await getATAAddress({
    mint: redemptionMint,
    owner: sdk.provider.wallet.publicKey,
  });

  const tx = await redeemer.redeemTokens({
    tokenAmount: amount,
    sourceAuthority: sdk.provider.wallet.publicKey,
    iouSource: iouATA,
    redemptionDestination: redemptionATA,
  });

  const receipt = await confirmTx(tx);

  printLog(receipt);

  return receipt;
}

function printLog(receipt: TransactionReceipt) {
  const events: Map<string, any> = new Map();
  QUARRY_CODERS.Redeemer.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  ).forEach((event) => {
    events.set(event.name, event.data);
  });
  const e = Object.fromEntries(events);

  printObjectJSON(
    {
      ...receiptLog(receipt),
      events: e,
    },
    2
  );
}
